import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Avatar,
  Divider,
  Rating,
  Box,
  FormControlLabel,
  Checkbox,
  DialogTitle,
  IconButton,
} from '@mui/material';
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { format } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { FiMaximize2, FiX } from 'react-icons/fi';
import VuiAvatar from "components/VuiAvatar";
import VuiBadge from "components/VuiBadge";
import ReactApexChart from "react-apexcharts";

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return format(new Date(dateString), 'PPpp');
};

const formatDuration = (seconds) => {
  if (!seconds) return 'N/A';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const formatDistance = (meters) => {
  if (!meters) return 'N/A';
  const kilometers = meters / 1000;
  return `${kilometers.toFixed(1)} km`;
};

const formatPrice = (price, currency) => {
  if (price === null || price === undefined) return 'N/A';
  return `${price} ${currency || 'SYP'}`;
};

// Helper function to format currency for display (can be used in tooltips)
const formatCurrencyDisplay = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// No longer needed for line chart points, but kept for display above the chart
const formatCurrency = (amount) => {
  return amount === null || amount === undefined ? 0 : amount;
};

// Helper function to calculate percentage difference
const calculatePercentageDiff = (value1, value2) => {
  const val1 = value1 === null || value1 === undefined ? 0 : value1;
  const val2 = value2 === null || value2 === undefined ? 0 : value2;

  if (val1 === 0 && val2 === 0) return 0;
  if (val1 === 0) return 100; // Or handle as infinite increase if appropriate
  return ((val2 - val1) / val1) * 100;
};

const Section = ({ title, children }) => (
  <VuiBox mb={3}>
    <VuiTypography variant="button" color="text" fontWeight="bold" mb={1}>
      {title}
    </VuiTypography>
    <VuiBox
      sx={{
        background: 'rgba(255,255,255,0.02)',
        borderRadius: '12px',
        p: 2,
      }}
    >
      {children}
    </VuiBox>
  </VuiBox>
);

const JourneyLocationMap = ({ source, destination, height = 200, fullscreen = false, onExpand, journeyPaths, showPaths }) => {
  if (!source?.latitude || !source?.longitude) return null;

  const sourcePosition = [parseFloat(source.latitude), parseFloat(source.longitude)];
  const destinationPosition = destination?.latitude && destination?.longitude
    ? [parseFloat(destination.latitude), parseFloat(destination.longitude)]
    : null;

  return (
    <div style={{ width: '100%', height, borderRadius: 12, overflow: 'hidden', zIndex: 1, position: 'relative' }}>
      {onExpand && !fullscreen && (
        <button
          onClick={onExpand}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1000,
            background: 'rgba(34,34,58,0.8)',
            border: 'none',
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
          title="Expand map"
        >
          <FiMaximize2 size={20} />
        </button>
      )}
      <MapContainer
        center={sourcePosition}
        zoom={13}
        style={{ width: '100%', height: '100%' }}
        scrollWheelZoom={true}
        dragging={true}
        doubleClickZoom={true}
        zoomControl={true}
        attributionControl={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; <a href='http://osm.org/copyright'>OpenStreetMap</a> contributors"
        />
        <Marker position={sourcePosition}>
          <Popup>
            {source.title || 'Source'}<br />
            {source.address}
          </Popup>
        </Marker>
        {destinationPosition && (
          <Marker position={destinationPosition}>
            <Popup>
              {destination.title || 'Destination'}<br />
              {destination.address}
            </Popup>
          </Marker>
        )}

        {/* Render Journey Paths */}
        {journeyPaths && (
          <>
            {/* Supposed Path (grey) */}
            {showPaths.supposed && journeyPaths.supposed_path && (
              <Polyline
                positions={journeyPaths.supposed_path}
                pathOptions={{
                  color: '#888',
                  weight: 3,
                  opacity: 0.8,
                  dashArray: '5, 10',
                }}
              />
            )}
            {/* Driver Arriving Path (blue) */}
            {showPaths.arriving && journeyPaths.driver_arriving_path && (
              <Polyline
                positions={journeyPaths.driver_arriving_path}
                pathOptions={{
                  color: '#2196f3',
                  weight: 3,
                  opacity: 0.8,
                }}
              />
            )}
            {/* Taken Path (green) */}
            {showPaths.taken && journeyPaths.taken_path && (
              <Polyline
                positions={journeyPaths.taken_path}
                pathOptions={{
                  color: '#43a047',
                  weight: 3,
                  opacity: 0.8,
                }}
              />
            )}
          </>
        )}
      </MapContainer>
    </div>
  );
};

const PriceComparisonChart = ({ estimatedCost, clientPrice, driverPrice }) => {
  const pricesData = [
    { label: "Estimated Cost", value: formatCurrency(estimatedCost) },
    { label: "Client Price", value: formatCurrency(clientPrice) },
    { label: "Driver Price", value: formatCurrency(driverPrice) }
  ];

  const chartSeries = [{
    name: "Price",
    data: pricesData.map(p => p.value)
  }];

  const chartOptions = {
    chart: {
      type: 'line',
      height: 150,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    colors: ['#2196f3'], // Use a single color for the line
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    grid: {
      borderColor: 'rgba(255, 255, 255, 0.1)',
      strokeDashArray: 2,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: pricesData.map(p => p.label),
      labels: {
        style: {
          colors: '#fff',
          fontSize: '12px',
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: '#fff',
          fontSize: '12px',
        },
        formatter: (value) => formatCurrencyDisplay(value) // Format Y-axis labels as currency
      },
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (value) => formatCurrencyDisplay(value) // Format tooltip value as currency
      }
    },
    markers: {
      size: 4,
      colors: ['#fff'],
      strokeColors: '#2196f3', // Marker border color
      strokeWidth: 2,
      hover: {
        size: 6,
      },
    },
  };

  // Calculate and display percentage differences above the chart labels
  const percentageDifferences = pricesData.map((price, index) => {
    if (index === 0) return null; // No difference for the first point
    const prevPrice = pricesData[index - 1].value;
    const currentPrice = price.value;
    return calculatePercentageDiff(prevPrice, currentPrice);
  });

  return (
    <VuiBox>
       <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={4} px={2}>
        {pricesData.map((price, index) => (
          <VuiBox key={price.label} textAlign="center" flex={1}>
            <VuiTypography variant="button" color="white" fontWeight="bold">
              {price.label}
            </VuiTypography>
             {percentageDifferences[index] !== null && (
              <VuiTypography
                variant="caption"
                color={percentageDifferences[index] >= 0 ? "success" : "error"}
                fontWeight="bold"
              >
                {percentageDifferences[index] >= 0 ? '+' : ''}{percentageDifferences[index].toFixed(1)}%
              </VuiTypography>
             )}
            <VuiTypography variant="h5" color={price.color} fontWeight="bold">
               {formatCurrencyDisplay(price.value)}
            </VuiTypography>
          </VuiBox>
        ))}
      </VuiBox>

      <VuiBox 
        sx={{ 
          height: '150px', 
          width: '100%',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          p: 1,
        }}
      >
         {/* ApexCharts component */}
         <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            width="100%"
            height="100%"
          />
      </VuiBox>
    </VuiBox>
  );
};

const JourneyDetailsModal = ({ open, onClose, journey }) => {
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [showPaths, setShowPaths] = useState({
    supposed: true,
    arriving: true,
    taken: true,
  });

  if (!journey) return null; // Ensure journey data is available

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#191932',
          color: '#fff',
          borderRadius: '18px',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <VuiTypography variant="h5" color="white" fontWeight="bold">
          Journey Details
        </VuiTypography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'white',
            background: 'rgba(34,34,58,0.8)',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s',
            '&:hover': {
              background: '#2196f3',
              color: '#fff',
            },
          }}
        >
          <FiX size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pt: 3, pb: 0 }}>
        {/* Journey Info */}
        <Section title="Journey Information">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                Status
              </VuiTypography>
              <VuiBadge
                variant="standard"
                badgeContent={journey.status}
                color={
                  journey.status === "Requested" ? "warning" :
                  journey.status === "Accepted" ? "info" :
                  journey.status === "Started" ? "primary" :
                  journey.status === "Ended_by_Client" ? "success" :
                  journey.status === "Ended_by_Driver" ? "success" :
                  journey.status === "Cancelled" ? "error" :
                  journey.status === "No_Driver_Found" ? "error" :
                  journey.status === "Driver_Cancelled" ? "error" :
                  journey.status === "Client_Cancelled" ? "error" :
                  "info"
                }
                size="xs"
                container
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                Created At
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {formatDate(journey.created_at)}
              </VuiTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                Estimated Duration
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {formatDuration(journey.estimated_duration)}
              </VuiTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                Estimated Distance
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {formatDistance(journey.estimated_distance)}
              </VuiTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                Estimated Cost
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {formatPrice(journey.estimated_cost, journey.en_currency)}
              </VuiTypography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <VuiTypography variant="caption" color="text" fontWeight="medium">
                Client Price
              </VuiTypography>
              <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                {formatPrice(journey.client_price, journey.en_currency)}
              </VuiTypography>
            </Grid>
          </Grid>
        </Section>

        {/* Price Comparison */}
        <Section title="Price Comparison">
          <PriceComparisonChart
            estimatedCost={journey.estimated_cost}
            clientPrice={journey.client_price}
            driverPrice={journey.driver_price}
          />
        </Section>

        {/* Client Info */}
        <Section title="Client Information">
          {journey.client ? (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}>
                <Avatar
                  src={journey.client.profile_photo}
                  alt={`${journey.client.first_name} ${journey.client.last_name}`}
                  sx={{ width: 64, height: 64, border: '3px solid #353570' }}
                />
              </Grid>
              <Grid item xs={12} sm={10}>
                <VuiTypography variant="button" color="white" fontWeight="bold">
                  {journey.client.first_name} {journey.client.last_name}
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  {journey.client.phone}
                </VuiTypography>
                <VuiBox display="flex" alignItems="center" mt={1}>
                  <Rating value={journey.client.rating} readOnly precision={0.5} size="small" />
                  <VuiTypography variant="caption" color="text" ml={1}>
                    ({journey.client.rating_count} reviews)
                  </VuiTypography>
                </VuiBox>
              </Grid>
            </Grid>
          ) : (
            <VuiTypography variant="caption" color="text">
              No client information available
            </VuiTypography>
          )}
        </Section>

        {/* Driver Info */}
        <Section title="Driver Information">
          {journey.driver ? (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}>
                <Avatar
                  src={journey.driver.profile_photo}
                  alt={`${journey.driver.first_name} ${journey.driver.last_name}`}
                  sx={{ width: 64, height: 64, border: '3px solid #353570' }}
                />
              </Grid>
              <Grid item xs={12} sm={10}>
                <VuiTypography variant="button" color="white" fontWeight="bold">
                  {journey.driver.first_name} {journey.driver.last_name}
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  {journey.driver.phone}
                </VuiTypography>
                <VuiBox display="flex" alignItems="center" mt={1}>
                  <Rating value={journey.driver.rating} readOnly precision={0.5} size="small" />
                  <VuiTypography variant="caption" color="text" ml={1}>
                    ({journey.driver.rating_count} reviews)
                  </VuiTypography>
                </VuiBox>
              </Grid>
            </Grid>
          ) : (
            <VuiTypography variant="caption" color="text">
              No driver assigned
            </VuiTypography>
          )}
        </Section>

        {/* Car Info */}
        {journey.car && (
          <Section title="Car Information">
            <Grid container spacing={2} alignItems="center">
              {journey.car.images && journey.car.images.length > 0 && (
                <Grid item xs={12} sm={2}>
                  <Avatar
                    src={journey.car.images[0]}
                    alt={`${journey.car.manufacturer} ${journey.car.model}`}
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '12px', // Slightly rounded corners for car image
                      objectFit: 'cover',
                      border: '3px solid #353570',
                      background: '#232345',
                    }}
                    variant="rounded"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={journey.car.images && journey.car.images.length > 0 ? 10 : 12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <VuiTypography variant="caption" color="text" fontWeight="medium">
                      Model
                    </VuiTypography>
                    <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                      {journey.car.model}
                    </VuiTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VuiTypography variant="caption" color="text" fontWeight="medium">
                      Manufacturer
                    </VuiTypography>
                    <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                      {journey.car.manufacturer}
                    </VuiTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VuiTypography variant="caption" color="text" fontWeight="medium">
                      Year
                    </VuiTypography>
                    <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                      {journey.car.year}
                    </VuiTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VuiTypography variant="caption" color="text" fontWeight="medium">
                      Plate Number
                    </VuiTypography>
                    <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ ml: 1 }}>
                      {journey.car.plate_number}
                    </VuiTypography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        )}

        {/* Location */}
        <Section title="Journey Location">
          {/* Path Toggles */}
          <VuiBox display="flex" gap={2} alignItems="center" mb={2}>
              <FormControlLabel
                control={<Checkbox checked={showPaths.supposed} onChange={e => setShowPaths(s => ({ ...s, supposed: e.target.checked }))} sx={{ color: '#888' }} />}
                label={<span style={{ color: '#888' }}>Supposed Path</span>}
              />
              <FormControlLabel
                control={<Checkbox checked={showPaths.arriving} onChange={e => setShowPaths(s => ({ ...s, arriving: e.target.checked }))} sx={{ color: '#2196f3' }} />}
                label={<span style={{ color: '#2196f3' }}>Driver Arriving Path</span>}
              />
              <FormControlLabel
                control={<Checkbox checked={showPaths.taken} onChange={e => setShowPaths(s => ({ ...s, taken: e.target.checked }))} sx={{ color: '#43a047' }} />}
                label={<span style={{ color: '#43a047' }}>Taken Path</span>}
              />
            </VuiBox>
          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={12}>
              <JourneyLocationMap
                source={{
                  latitude: journey.source_latitude,
                  longitude: journey.source_longitude,
                  title: journey.source_title,
                  address: journey.source_address,
                }}
                destination={{
                  latitude: journey.destination_latitude,
                  longitude: journey.destination_longitude,
                  title: journey.destination_title,
                  address: journey.destination_address,
                }}
                journeyPaths={{
                  supposed_path: journey.supposed_path,
                  driver_arriving_path: journey.driver_arriving_path,
                  taken_path: journey.taken_path,
                }}
                showPaths={showPaths}
                onExpand={() => setMapFullscreen(true)}
              />
            </Grid>
          </Grid>
        </Section>

        {/* Ratings */}
        {journey.ratings && journey.ratings.length > 0 && (
          <Section title="Ratings & Reviews">
            {journey.ratings.map((rating, index) => (
              <VuiBox key={index} mb={2} p={2} display="flex" alignItems="flex-start" gap={2} sx={{ background: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
                <Avatar
                  src={rating.role === 'client' ? journey.client?.profile_photo : journey.driver?.profile_photo}
                  alt={rating.role === 'client' ? `${journey.client?.first_name} ${journey.client?.last_name}` : `${journey.driver?.first_name} ${journey.driver?.last_name}`}
                  sx={{ width: 40, height: 40, border: '2px solid #353570' }}
                />
                <VuiBox flex={1}>
                  <VuiTypography variant="button" color="white" fontWeight="bold">
                    {rating.role === 'client' ? `${journey.client?.first_name} ${journey.client?.last_name}` : `${journey.driver?.first_name} ${journey.driver?.last_name}`}
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text" ml={1}>
                    {formatDate(rating.created_at)}
                  </VuiTypography>
                  <VuiBox display="flex" alignItems="center" gap={1} mt={0.5} mb={0.5}>
                    <Rating value={rating.rating_value} readOnly size="small" />
                  </VuiBox>
                  <VuiTypography variant="caption" color="white">
                    {rating.review}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            ))}
          </Section>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JourneyDetailsModal; 